import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, GraduationCap, Loader2, Users } from "lucide-react";
import { api } from "../lib/api";
import { Reveal, Chapter } from "../components/Reveal";

const COURSE_PATHS = {
  "English Literature": "/english-literature-courses",
  "AI and Digital Turn in Literature": "/ai-digital-literature-courses",
  IELTS: "/ielts-courses",
  TOEFL: "/toefl-courses",
  "English Language": "/english-language-courses",
  "Quranic Studies": "/quranic-studies",
};

const initials = (name) =>
  name.replace(/^Dr\.?\s+/i, "").split(" ").map((w) => w[0]).filter((c) => c && c === c.toUpperCase()).slice(0, 2).join("");

function FacultyCard({ tutor, index }) {
  return (
    <div data-testid={`faculty-card-${index}`}
      className="flex h-full flex-col rounded-3xl border border-border bg-white p-7 transition-[transform,box-shadow,border-color] duration-300 hover:-translate-y-1.5 hover:border-gold/40 hover:shadow-[0_22px_55px_rgba(10,41,30,0.1)]">
      <div className="flex items-center gap-4">
        <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-forest font-display text-xl font-extrabold text-bone">
          {initials(tutor.name)}
        </span>
        <div>
          <h3 className="font-display text-xl font-bold leading-snug text-forest-deep" data-testid={`faculty-name-${index}`}>{tutor.name}</h3>
          {tutor.qualification && (
            <p className="mt-1 flex items-center gap-1.5 text-sm font-medium text-forest/80" data-testid={`faculty-qualification-${index}`}>
              <GraduationCap className="h-4 w-4 text-gold-dark" /> {tutor.qualification}
            </p>
          )}
        </div>
      </div>
      {tutor.institution && (
        <p className="mt-4 text-sm leading-relaxed text-muted-foreground" data-testid={`faculty-institution-${index}`}>{tutor.institution}</p>
      )}
      {tutor.bio && <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{tutor.bio}</p>}
      <div className="mt-6 flex-1 border-t border-border pt-5">
        <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-gold-dark">Assigned courses</p>
        <div className="mt-3 flex flex-wrap gap-2" data-testid={`faculty-courses-${index}`}>
          {(tutor.courses || []).map((c) =>
            COURSE_PATHS[c] ? (
              <Link key={c} to={COURSE_PATHS[c]}
                className="group flex items-center gap-1 rounded-full bg-secondary px-3 py-1.5 text-xs font-semibold text-forest transition-colors hover:bg-gold/15 hover:text-gold-dark">
                {c} <ArrowUpRight className="h-3 w-3 transition-transform duration-200 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
              </Link>
            ) : (
              <span key={c} className="rounded-full bg-secondary px-3 py-1.5 text-xs font-semibold text-forest">{c}</span>
            )
          )}
        </div>
      </div>
    </div>
  );
}

export default function TutorsPage() {
  const [faculty, setFaculty] = useState(null);

  useEffect(() => {
    api.get("/content/faculty").then((r) => setFaculty(r.data)).catch(() => setFaculty([]));
  }, []);

  return (
    <div className="min-h-screen bg-bone pt-[72px]" data-testid="tutors-page">
      <div className="relative overflow-hidden bg-forest-deep">
        <div className="bg-noise pointer-events-none absolute inset-0" />
        <div className="relative mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8 lg:py-20">
          <Reveal>
            <nav className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-emerald-100/50" data-testid="tutors-breadcrumb">
              <Link to="/" className="transition-colors hover:text-bone">Home</Link>
              <span>/</span>
              <span className="text-gold">Tutors</span>
            </nav>
          </Reveal>
          <Reveal delay={0.08}>
            <h1 className="mt-6 max-w-3xl font-display text-4xl font-extrabold tracking-tight text-bone sm:text-5xl">
              Meet our{" "}
              <em className="font-editorial font-medium italic text-gold">faculty.</em>
            </h1>
          </Reveal>
          <Reveal delay={0.16}>
            <p className="mt-5 max-w-2xl text-base leading-relaxed text-emerald-100/70 sm:text-lg">
              Doctoral-qualified specialists in literature, linguistics, and language teaching who lead every LearnYourWay course.
            </p>
          </Reveal>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-4 py-14 sm:px-6 lg:px-8">
        <Reveal>
          <Chapter index="01" label="Our tutors" />
        </Reveal>
        <div className="mt-8 grid gap-5 md:grid-cols-2" data-testid="faculty-grid">
          {faculty === null ? (
            <div className="col-span-full flex justify-center py-16"><Loader2 className="h-7 w-7 animate-spin text-forest" /></div>
          ) : faculty.length === 0 ? (
            <p className="col-span-full rounded-3xl border border-dashed border-border bg-white py-14 text-center text-sm text-muted-foreground" data-testid="faculty-empty">
              <Users className="mx-auto mb-3 h-7 w-7 text-forest/25" /> Tutor profiles are coming soon.
            </p>
          ) : (
            faculty.map((t, i) => (
              <Reveal key={t.id} delay={(i % 2) * 0.08}>
                <FacultyCard tutor={t} index={i} />
              </Reveal>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
